import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { cookieManager } from '../../Constant/Utils';
import { message } from 'antd';
import {
  clearCheckoutSnapshot,
  readPendingCheckoutSnapshot,
} from '../../utils/campaignDraft';
import {
  getSubmitCampaignErrorMessage,
  submitPaidCampaign,
} from '../../utils/campaignCreateAfterPayment';
import { getArtistAuthHeaders } from '../../utils/artistSession';

const initialState = {
  campaigns: [],
  campaignsStatus: 'idle',
  campaignsError: null,
  notification: false,
  drafts: [],
};

const CAMPAIGN_DRAFTS_URL = `${process.env.REACT_APP_BACKEND_URI}/artistCampaign/drafts`;

export const getArtistCampaigns = createAsyncThunk(
  'artistSlice/getArtistCampaigns',
  async (_, { rejectWithValue }) => {
    try {
      let auth0User = JSON.parse(localStorage.getItem('auth0-user'));
      let config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_BACKEND_URI}/artistCampaign/${auth0User?.email}/campaignData?userId=${auth0User?.sub}`,
        headers: {},
      };
      const response = await axios.request(config);
      return response?.data?.data;
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to fetch campaigns');
    }
  }
);

export const updateCampaignAfterAuth = createAsyncThunk(
  'artistSlice/updateCampaignAfterAuth',
  async (callback, { rejectWithValue }) => {
    try {
      const campaignData = readPendingCheckoutSnapshot();
      if (!campaignData) return null;

      const token = cookieManager.getCookie('token');
      const auth0User = JSON.parse(localStorage.getItem('auth0-user') || 'null');

      if (!campaignData._id) {
        const stripeSessionId = campaignData.stripeSessionId?.toString?.().trim?.() || '';
        if (!stripeSessionId) {
          localStorage.removeItem('unAuthUserCampaign');
          return null;
        }

        const session = {
          email: auth0User?.email || campaignData.artist_email || '',
          userId: auth0User?.sub || campaignData.userId || '',
        };

        const response = await submitPaidCampaign({
          campaignData,
          stripeSessionId,
          session,
        });
        clearCheckoutSnapshot();
        if (callback) callback();
        return response?.data;
      }

      const campaignId = campaignData._id;
      const updateData = { ...campaignData };
      delete updateData._id;

      updateData.userId = auth0User?.sub || updateData.userId || '';
      updateData.artist_email = auth0User?.email || updateData.artist_email || '';
      updateData.updated_at = new Date().toISOString();

      const config = {
        method: 'put',
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_BACKEND_URI}/artistCampaign/updateCampaign/${campaignId}`,
        headers: {
          'Content-Type': 'application/json',
          authorization: token,
        },
        data: JSON.stringify(updateData),
      };

      const response = await axios.request(config);
      clearCheckoutSnapshot();
      if (callback) callback();
      return response?.data?.data;
    } catch (error) {
      const errorMsg = getSubmitCampaignErrorMessage(error);
      message.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

export const getCampaignDrafts = createAsyncThunk(
  'artistSlice/getCampaignDrafts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(CAMPAIGN_DRAFTS_URL, { headers: getArtistAuthHeaders() });
      return response?.data?.data || [];
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to fetch drafts');
    }
  }
);

// Mirrors the wizard's local draft to the server so it is listed under Drafts and expires after 3 days.
export const saveCampaignDraft = createAsyncThunk(
  'artistSlice/saveCampaignDraft',
  async (draft, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${CAMPAIGN_DRAFTS_URL}/${draft.draftKey}`, draft, {
        headers: getArtistAuthHeaders(),
      });
      return response?.data?.data || null;
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to save draft');
    }
  }
);

export const deleteCampaignDraft = createAsyncThunk(
  'artistSlice/deleteCampaignDraft',
  async (draftKey, { rejectWithValue }) => {
    try {
      await axios.delete(`${CAMPAIGN_DRAFTS_URL}/${draftKey}`, { headers: getArtistAuthHeaders() });
      return draftKey;
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to delete draft');
    }
  }
);

export const getNotfication = createAsyncThunk(
  'artistSlice/getNotfication',
  async (_, { rejectWithValue }) => {
    try {
      return localStorage.getItem('notification');
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to read notification state');
    }
  }
);

export const artistSlice = createSlice({
  name: 'artistSlice',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getArtistCampaigns.pending, (state, action) => {
        state.campaignsStatus = 'loading';
        state.campaignsError = null;
      })
      .addCase(getArtistCampaigns.fulfilled, (state, action) => {
        state.campaigns = action.payload;
        state.campaignsStatus = 'succeeded';
      })
      .addCase(getArtistCampaigns.rejected, (state, action) => {
        state.campaignsStatus = 'failed';
        state.campaignsError = action.payload || action.error?.message || 'Failed to fetch campaigns';
      })
      .addCase(getCampaignDrafts.fulfilled, (state, action) => {
        state.drafts = action.payload;
      })
      .addCase(saveCampaignDraft.fulfilled, (state, action) => {
        const saved = action.payload;
        if (!saved) return;
        state.drafts = [saved, ...state.drafts.filter(draft => draft.draftKey !== saved.draftKey)];
      })
      .addCase(deleteCampaignDraft.fulfilled, (state, action) => {
        state.drafts = state.drafts.filter(draft => draft.draftKey !== action.payload);
      })
      .addCase(getNotfication.pending, (state, action) => {})
      .addCase(getNotfication.fulfilled, (state, action) => {
        state.notification = action.payload;
      })
      .addCase(getNotfication.rejected, (state, action) => {})
      .addCase(updateCampaignAfterAuth.fulfilled, (state, action) => {
        if (action.payload) {
          message.success('Campaign updated successfully');
        }
      })
      .addCase(updateCampaignAfterAuth.rejected, (state, action) => {
        // Error is already handled in the thunk
      });
  },
});

export default artistSlice.reducer;
